import React from 'react';
import { motion } from 'framer-motion';
import { Check, Gift, Star, ShieldCheck, Zap, Lock } from 'lucide-react';
import { SectionHead, CtaButton, SCROLL_MT } from './shared';

const CHIP_ICONS = [Zap, ShieldCheck, Lock];

function Row({ item, index, bonus = false }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }}
      className={`flex items-start justify-between gap-4 rounded-2xl px-4 sm:px-5 py-4 transition ${bonus ? 'bg-amber-400/[0.07] border border-amber-300/20' : 'bg-white/5 border border-white/10 hover:bg-white/[0.08]'}`}
      data-testid={bonus ? `inside-bonus-${index}` : `inside-item-${index}`}>
      <div className="flex items-start gap-3.5 min-w-0">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${bonus ? 'bg-amber-400/20 text-amber-300' : 'bg-emerald-500/15 text-emerald-400'}`}>
          {bonus ? <Star size={13} fill="currentColor" /> : <Check size={15} />}
        </span>
        <div className="min-w-0">
          <p className="font-bold text-[15px] leading-snug">{item.title}</p>
          {item.desc && <p className="text-[13px] text-white/55 mt-1 leading-relaxed">{item.desc}</p>}
        </div>
      </div>
      {item.value && <span className="shrink-0 text-[13px] font-bold text-white/60 mt-1 tabular-nums">{item.value}</span>}
    </motion.div>
  );
}

export default function OfferStack({ inside, onCta, onSecondary, secondaryLabel }) {
  const items = (inside.items || []).filter(i => i && i.title);
  const bonuses = (inside.bonuses || []).filter(i => i && i.title);
  const chips = (inside.trust_chips || []).filter(Boolean).slice(0, 3);
  return (
    <section id="whats-inside" className={`${SCROLL_MT} py-12 sm:py-16 lg:py-20`} data-testid="whats-inside-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <SectionHead label={inside.label} title={inside.title} className="mb-8 sm:mb-10" />
        <div className="relative rounded-[30px] p-[2px] bg-gradient-to-br from-amber-400/60 via-slate-700 to-primary/60 shadow-[0_35px_90px_-30px_rgba(15,23,42,0.65)]">
          <div className="rounded-[28px] bg-slate-900 text-white p-5 sm:p-8 lg:p-10">
            <div className="grid lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] gap-8 lg:gap-10 items-start">
              <div>
                <div className="space-y-3">
                  {items.map((item, i) => <Row key={i} item={item} index={i} />)}
                </div>
                {bonuses.length > 0 && (
                  <>
                    <p className="mt-7 mb-3 text-[12px] tracking-[0.22em] font-bold text-amber-400 flex items-center gap-2"><Gift size={14} /> BONUSES</p>
                    <div className="space-y-3">
                      {bonuses.map((item, i) => <Row key={i} item={item} index={i} bonus />)}
                    </div>
                  </>
                )}
              </div>

              <aside className="lg:sticky lg:top-28 rounded-[22px] bg-white/[0.06] border border-white/10 p-6 sm:p-7" data-testid="inside-price-panel">
                {inside.total_value && (
                  <p className="text-sm text-white/55">Total value <span className="line-through font-semibold text-white/75 ml-1">{inside.total_value}</span></p>
                )}
                {inside.price_label && <p className="mt-3 text-white/80 text-sm">{inside.price_label}</p>}
                <p className="mt-1 font-[Manrope] text-[2.1rem] sm:text-[2.5rem] leading-[1.05] font-extrabold tracking-tight text-white" data-testid="inside-price">{inside.price}</p>
                {inside.price_note && <p className="mt-3 text-[13px] text-white/55 leading-relaxed">{inside.price_note}</p>}
                <div className="mt-6">
                  <CtaButton label={inside.cta || 'Get Full Access'} onClick={() => onCta('whats_inside')} variant="amber" size="md" className="w-full" testId="whats-inside-cta" />
                </div>
                {onSecondary && (
                  <button onClick={() => onSecondary('whats_inside')} className="mt-3 w-full text-center text-[12.5px] text-white/60 hover:text-white underline underline-offset-4 decoration-white/30 transition" data-testid="whats-inside-secondary">
                    {secondaryLabel || 'Try one free mock test first'} →
                  </button>
                )}
                {chips.length > 0 && (
                  <ul className="mt-6 pt-5 border-t border-white/10 grid grid-cols-3 gap-2" data-testid="inside-trust-chips">
                    {chips.map((c, i) => { const Icon = CHIP_ICONS[i % 3]; return (
                      <li key={i} className="flex flex-col items-center gap-1.5 text-center text-[11px] font-semibold text-white/60">
                        <Icon size={15} className="text-amber-400" />{c}
                      </li>
                    ); })}
                  </ul>
                )}
              </aside>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
